import { SiGoogle } from "@icons-pack/react-simple-icons";
import { useCallback, useState } from "react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import { authFeatures } from "./auth-features";
import { redirectSocialSignIn } from "./oauth-resume";

interface GoogleSignInButtonProps {
  onError?: (error: string) => void;
}

// Web and native share the redirect flow: better-auth returns the Google auth
// URL, and on native the capacitor client plugin opens it in the system
// browser (Google forbids OAuth inside embedded WebViews), deep-linking back
// via vidimo://auth/callback.
export function GoogleSignInButton({ onError }: GoogleSignInButtonProps) {
  const t = useTranslations("auth.googleSignInButton");
  const [loading, setLoading] = useState(false);
  // Hidden unless the build says Google sign-in is configured, so environments
  // without Google credentials never show a button that dead-ends on click.
  const { googleSignInEnabled } = authFeatures;

  const handleClick = useCallback(async () => {
    setLoading(true);
    const error = await redirectSocialSignIn("google");
    // On success the page navigates to Google; loading intentionally stays on
    // until the navigation takes over.
    if (error) {
      setLoading(false);
      onError?.(error);
    }
  }, [onError]);

  if (!googleSignInEnabled) {
    return null;
  }

  return (
    <Button
      className="w-full"
      disabled={loading}
      onClick={handleClick}
      type="button"
      variant="outline"
    >
      <SiGoogle className="size-4" />
      {loading ? t("signingIn") : t("continue")}
    </Button>
  );
}
